import { useEffect, useRef, useState } from "react";

interface ChallengeConfiguration {
  enabled: boolean;
  provider: string | null;
  siteKey: string | null;
}

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileScript: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (turnstileScript) return turnstileScript;
  turnstileScript = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-evileye-turnstile]");
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("challenge-script-failed")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.evileyeTurnstile = "true";
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("challenge-script-failed")), { once: true });
    document.head.append(script);
  });
  return turnstileScript;
}

export function HumanChallenge({
  action,
  onChange,
  resetKey = 0,
}: {
  action: "registration" | "recovery" | "appeal";
  onChange: (state: { ready: boolean; token: string | null }) => void;
  resetKey?: number;
}) {
  const container = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const [configuration, setConfiguration] = useState<ChallengeConfiguration | null>(null);
  const [failed, setFailed] = useState(false);
  onChangeRef.current = onChange;

  useEffect(() => {
    const controller = new AbortController();
    setFailed(false);
    setConfiguration(null);
    onChangeRef.current({ ready: false, token: null });
    void fetch("/api/v1/challenge/configuration", {
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("challenge-configuration-failed");
        return (await response.json()) as ChallengeConfiguration;
      })
      .then((value) => {
        setConfiguration(value);
        if (!value.enabled) onChangeRef.current({ ready: true, token: null });
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setFailed(true);
          onChangeRef.current({ ready: false, token: null });
        }
      });
    return () => controller.abort();
  }, [action, resetKey]);

  useEffect(() => {
    if (!configuration?.enabled || configuration.provider !== "turnstile"
        || !configuration.siteKey || !container.current) return;
    let widgetId: string | null = null;
    let active = true;
    void loadTurnstile()
      .then(() => {
        if (!active || !container.current || !window.turnstile) return;
        widgetId = window.turnstile.render(container.current, {
          sitekey: configuration.siteKey,
          action,
          theme: "auto",
          callback: (token: string) => onChangeRef.current({ ready: true, token }),
          "expired-callback": () => onChangeRef.current({ ready: false, token: null }),
          "error-callback": () => {
            setFailed(true);
            onChangeRef.current({ ready: false, token: null });
          },
        });
      })
      .catch(() => {
        setFailed(true);
        onChangeRef.current({ ready: false, token: null });
      });
    return () => {
      active = false;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [action, configuration]);

  if (failed) return <div className="challenge-error" role="alert">人机验证暂时不可用，请稍后重试。</div>;
  if (!configuration?.enabled) return null;
  return <div className="challenge-slot" ref={container} />;
}
